import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { Eye, EyeOff, LogIn } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BG_IMAGE = "https://customer-assets.emergentagent.com/job_37d91874-055e-4733-b3dd-bdd99f83cc88/artifacts/1sq98tty_freepik__create-an-background-image-without-the-login-based__59111.jpeg";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, { email, password });
      const { access_token, user_name, user_email } = response.data;
      login(access_token, { name: user_name, email: user_email });
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="relative min-h-screen w-full flex flex-col items-center"
      style={{
        backgroundImage: `url(${BG_IMAGE})`,
        backgroundSize: "cover",
        backgroundPosition: "center bottom",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* App Name at top center */}
      <div className="w-full flex justify-center pt-8 pb-4 z-10">
        <h1
          data-testid="app-title"
          className="serenity-title"
          style={{
            fontFamily: "'Great Vibes', cursive",
            fontSize: "clamp(3rem, 7vw, 5rem)",
            color: "#1A4D2E",
            fontWeight: "400",
            textShadow: "none",
            letterSpacing: "0.02em",
            lineHeight: 1.2,
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            textRendering: "optimizeLegibility",
            transform: "translateZ(0)",
            WebkitTransform: "translateZ(0)",
          }}
        >
          Serenity
        </h1>
      </div>

      {/* Login Card */}
      <div className="flex-1 flex items-center justify-center w-full px-4 pb-16">
        <div
          data-testid="login-card"
          className="w-full max-w-md"
          style={{
            background: "rgba(255, 255, 255, 0.78)",
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
            borderRadius: "1.75rem",
            border: "1px solid rgba(255,255,255,0.5)",
            boxShadow: "0 8px 48px rgba(26,77,46,0.12), 0 2px 16px rgba(0,0,0,0.08)",
            padding: "2.5rem 2.25rem",
          }}
        >
          <div className="text-center mb-8">
            <p
              style={{
                fontFamily: "'Manrope', sans-serif",
                color: "#1A4D2E",
                fontSize: "1rem",
                fontWeight: 500,
                opacity: 0.75,
              }}
            >
              Sign in to your account
            </p>
          </div>

          <form onSubmit={handleSubmit} data-testid="login-form" className="space-y-5">
            {/* Email */}
            <div>
              <label
                htmlFor="email"
                style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.875rem", fontWeight: 600 }}
                className="block mb-1.5"
              >
                Email Address
              </label>
              <input
                id="email"
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="admin@serenity.com"
                style={{
                  fontFamily: "'Manrope', sans-serif",
                  width: "100%",
                  padding: "0.75rem 1rem",
                  borderRadius: "0.75rem",
                  border: "1.5px solid rgba(26,77,46,0.2)",
                  background: "rgba(255,255,255,0.65)",
                  color: "#0F172A",
                  fontSize: "0.95rem",
                  outline: "none",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#1A4D2E";
                  e.target.style.boxShadow = "0 0 0 3px rgba(26,77,46,0.12)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "rgba(26,77,46,0.2)";
                  e.target.style.boxShadow = "none";
                }}
              />
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                style={{ fontFamily: "'Manrope', sans-serif", color: "#1A4D2E", fontSize: "0.875rem", fontWeight: 600 }}
                className="block mb-1.5"
              >
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  data-testid="password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                  style={{
                    fontFamily: "'Manrope', sans-serif",
                    width: "100%",
                    padding: "0.75rem 3rem 0.75rem 1rem",
                    borderRadius: "0.75rem",
                    border: "1.5px solid rgba(26,77,46,0.2)",
                    background: "rgba(255,255,255,0.65)",
                    color: "#0F172A",
                    fontSize: "0.95rem",
                    outline: "none",
                    transition: "border-color 0.2s, box-shadow 0.2s",
                    boxSizing: "border-box",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#1A4D2E";
                    e.target.style.boxShadow = "0 0 0 3px rgba(26,77,46,0.12)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "rgba(26,77,46,0.2)";
                    e.target.style.boxShadow = "none";
                  }}
                />
                <button
                  type="button"
                  data-testid="toggle-password-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute",
                    right: "0.85rem",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#1A4D2E",
                    opacity: 0.6,
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div
                data-testid="login-error"
                style={{
                  fontFamily: "'Manrope', sans-serif",
                  background: "rgba(220,38,38,0.08)",
                  border: "1px solid rgba(220,38,38,0.25)",
                  borderRadius: "0.625rem",
                  padding: "0.625rem 0.875rem",
                  color: "#DC2626",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              style={{
                fontFamily: "'Manrope', sans-serif",
                width: "100%",
                padding: "0.8rem",
                borderRadius: "3rem",
                background: loading ? "#6aac85" : "#1A4D2E",
                color: "#FFFFFF",
                fontSize: "1rem",
                fontWeight: 600,
                border: "none",
                cursor: loading ? "not-allowed" : "pointer",
                transition: "background 0.25s, transform 0.15s, box-shadow 0.25s",
                boxShadow: "0 4px 20px rgba(26,77,46,0.3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
                letterSpacing: "0.02em",
                marginTop: "0.25rem",
              }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = "#143D24"; }}
              onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = "#1A4D2E"; }}
            >
              {loading ? (
                <span>Signing in...</span>
              ) : (
                <>
                  <LogIn size={18} />
                  <span>Sign In</span>
                </>
              )}
            </button>
          </form>

          {/* Hint */}
          <p
            style={{
              fontFamily: "'Manrope', sans-serif",
              textAlign: "center",
              marginTop: "1.5rem",
              color: "#64748B",
              fontSize: "0.8rem",
            }}
          >
            Use your admin credentials to sign in
          </p>
        </div>
      </div>
    </div>
  );
}
